
import { ParamTypes, StateService, Transition } from '@uirouter/angular';

import { AccountLicenseService } from 'rev-shared/security/AccountLicense.Service';
import { IVbNg2StateDeclaration } from 'rev-shared/ts-utils/IVbNg2StateDeclaration';
import { LoginRedirectService } from 'rev-shared/security/LoginRedirect.Service';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { PublicVideoPasswordComponent } from 'rev-shared/media/publicVideoPassword/PublicVideoPassword.Component';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UserLocalIPService } from 'rev-shared/security/UserLocalIP.Service';
import { VideoService } from 'rev-shared/media/Video.Service';

import { IVideoPlaybackConfig } from './IVideoPlaybackConfig';
import { PUBLIC_VIDEO_PASSWORD_STATE_NAME, VIDEO_PLAYBACK_STATE_NAME, VIDEO_PLAYLIST_PLAYBACK_STATE_NAME } from './Constants';
import { VideoPlaybackComponent } from './VideoPlayback.Component';
import { VideoPlaybackService } from './VideoPlayback.Service';

const defaultConfig: IVideoPlaybackConfig = {
	showApprovalProcess: true,
	showSidebar: true,
	showSettingsMenu: true
};

const playbackResolve = [
	{
		token: 'mediaFeatures',
		deps: [MediaFeaturesService],
		resolveFn: (MediaFeatures: MediaFeaturesService) => MediaFeatures.getFeatures()
	},
	{
		token: 'loadAccountLicense',
		deps: [AccountLicenseService],
		resolveFn: (accountLicense: AccountLicenseService) => accountLicense.reload()
	},
	{
		token: 'hasMediaEditAuth',
		deps: [SecurityContextService],
		resolveFn: (SecurityContext: SecurityContextService) => !!SecurityContext.checkAuthorization('media.edit')
	},
	{
		token: 'checkUserLocation',
		deps: [UserLocalIPService],
		resolveFn: (UserLocalIPService: UserLocalIPService) => UserLocalIPService.checkUserLocation()
	},
	{
		token: 'isMediaAdmin',
		deps: [SecurityContextService],
		resolveFn: (SecurityContext: SecurityContextService) => !!SecurityContext.checkAuthorization('admin.media.approvalProcesses')
	},
	{
		token: 'authorizeMediaView',
		deps: [SecurityContextService],
		resolveFn: (SecurityContext: SecurityContextService) => SecurityContext.checkAuthorization('media.view')
	},
	{
		token: 'config',
		deps: [Transition],
		resolveFn: (transition: Transition): IVideoPlaybackConfig => {
			const config = transition.params().config;
			return {
				...defaultConfig,
				...config,
				initialSidebarButton: transition.params().tab
			};
		}
	}
];

const playbackOnEnter = (transition: Transition) => {
	const injector = transition.injector();
	const config = injector.get<IVideoPlaybackConfig>('config');

	return injector.getAsync<VideoPlaybackService>(VideoPlaybackService)
		.then(svc => {
			svc.start(transition.from().name, config);
		});
};

const playbackOnExit = (transition: Transition) => {
	const injector = transition.injector();

	return injector.getAsync<VideoPlaybackService>(VideoPlaybackService)
		.then(svc => {
			svc.stop();
		});
};

export const states: IVbNg2StateDeclaration[] = [
	{
		name: VIDEO_PLAYBACK_STATE_NAME,
		url: '/videos/:videoId?startAt&autoplay&tab',
		component: VideoPlaybackComponent,
		allowPublicVideoAccess: true,
		allowRegisteredGuestAccess: true,
		resetZoom: true,
		params: {
			autoplay: { value: null, squash: true, type: ParamTypes.bool, dynamic: true },
			startAt: { value: null, squash: true, type: ParamTypes.string, dynamic: true },
			config: { value: null, squash: true, type: ParamTypes.json, dynamic: true },
			tab: { value: null, squash: true, type: ParamTypes.string, dynamic: true }
		},
		resolve: [
			...playbackResolve,
			{
				token: 'previousState',
				deps: [Transition],
				resolveFn: (transition: Transition, config: IVideoPlaybackConfig) => { return config?.showCloseBtn ? { name: transition.from().name, params: transition.params('from') } : {}; }
			}
		],
		onEnter: playbackOnEnter,
		onExit: playbackOnExit
	},
	{
		name: VIDEO_PLAYLIST_PLAYBACK_STATE_NAME,
		url: '/playlist/:playlistId/videos/?videoId',
		component: VideoPlaybackComponent,
		resetZoom: true,
		params: {
			videoId: {
				dynamic: true,
				type: ParamTypes.string,
				value: null
			}
		},
		resolve: [...playbackResolve],
		onEnter: playbackOnEnter,
		onExit: playbackOnExit
	},
	{
		name: PUBLIC_VIDEO_PASSWORD_STATE_NAME,
		component: PublicVideoPasswordComponent,
		allowPublicVideoAccess: true,
		allowRegisteredGuestAccess: true,
		resetZoom: true,
		params: {
			videoId: {},
			isPasswordIncorrect: { value: false, type: ParamTypes.bool },
			playbackConfig: { value: null, type: ParamTypes.json }
		},
		resolve: [
			{
				token: 'videoId',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().videoId
			},
			{
				token: 'playbackConfig',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().playbackConfig
			},
			{
				token: 'cookieKey',
				deps: ['videoId'],
				resolveFn: (videoId: string) => `${videoId}-auth`
			},
			{
				token: 'videoInfo',
				deps: [VideoService, 'videoId'],
				resolveFn: (videoService: VideoService, videoId: string) => videoService.getVideoBasicInfo(videoId)
			},
			{
				token: 'loginRedirect',
				deps: [LoginRedirectService, StateService, 'videoId'],
				resolveFn(loginRedirectService: LoginRedirectService, $state: StateService, videoId: string) {
					const videoRedirect = $state.href(VIDEO_PLAYBACK_STATE_NAME, { videoId }).substr(1); // remove the leading #
					return () => loginRedirectService.redirectToLogin(videoRedirect);
				}
			},
			{
				token: 'submitRedirect',
				deps: [StateService, 'videoId', 'playbackConfig'],
				resolveFn($state: StateService, videoId: string, playbackConfig: IVideoPlaybackConfig) {
					return () => $state.go(VIDEO_PLAYBACK_STATE_NAME, { videoId, config: playbackConfig });
				}
			},
			{
				token: 'isPasswordIncorrect',
				deps: [Transition],
				resolveFn: (transition: Transition) => transition.params().isPasswordIncorrect
			}
		]
	}
];
